import { ESdChangeReason } from "@kuika/kuika-cl-model";
import type {
  IConditionalStyle,
  IDisplayItem,
  IFillStyle,
  IFilterItem,
  IKuikaStyle,
  ILoopingAnimationItem,
  ITransformItem,
  ITransitionItem,
  IScreenDesign,
  IStylingBorder,
  IStylingEffects,
  IStylingPosition,
  IStylingShadow,
  IStylingTypography,
  StyleState
} from "@kuika/kuika-cl-model";
import _ from "lodash";

/**
 * Stateless helpers extracted from `styling-window.tsx` so they can be
 * unit-tested without dragging the antd Form / React class component
 * import chain into the test runtime. None of these functions touch
 * StylingWindow's instance state — they take their inputs explicitly and
 * return new values (or mutate the caller-provided target on the
 * "preserve" / "save" paths, which is what styling-window already
 * relied on).
 */

/** Sections the styling panel knows how to read/write, in canonical order. */
export const STYLE_SECTIONS: readonly (keyof IKuikaStyle)[] = [
  "position",
  "text",
  "fill",
  "border",
  "shadow",
  "effects"
] as const;

/**
 * Per-section allow-list of CSS keys that may be lifted into state-mode
 * sub-objects (hoverStyles, pressedStyles, focusedStyles, disabledStyles).
 * Anything outside this list — layout/structural keys, ID refs, control
 * metadata — must stay on the base section because applying it
 * pseudo-class-conditionally would tear the responsive grid apart.
 */
export const STATE_ALLOWED_PROPS: Readonly<Record<string, readonly string[]>> = {
  position: [
    "marginTop",
    "marginRight",
    "marginBottom",
    "marginLeft",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "width",
    "height",
    "maxWidth",
    "maxHeight",
    "minWidth",
    "minHeight",
    "alignItems",
    "textAlign",
    "xAxis",
    "yAxis"
  ],
  text: [
    "fontFamily",
    "fontWeight",
    "fontStyle",
    "fontSize",
    "color",
    "lineHeight",
    "letterSpacing",
    "textDecoration",
    "textTransform"
  ],
  fill: ["color", "backgroundImage"],
  border: [
    "borderColor",
    "borderTopWidth",
    "borderRightWidth",
    "borderBottomWidth",
    "borderLeftWidth",
    "borderTopLeftRadius",
    "borderTopRightRadius",
    "borderBottomRightRadius",
    "borderBottomLeftRadius"
  ],
  shadow: ["color", "x", "y", "b", "s", "isInset"],
  effects: [
    "opacity",
    "cursor",
    "pointerEvents",
    "mixBlendMode",
    "outlineStyle",
    "outlineWidth",
    "outlineColor",
    "outlineOffset",
    // Faz 3: multi-item arrays + Transform Settings scalars
    "transforms",
    "transitions",
    "transformOriginX",
    "transformOriginY",
    "perspective",
    "childrenPerspective",
    "childrenPerspectiveOriginX",
    "childrenPerspectiveOriginY",
    "backfaceVisibility",
    "transformStyle",
    // Faz 4: filter chain + backdrop-filter
    "filters",
    "backdropFilters",
    // Looping animations — per-state so "spin on hover" etc. routes to the
    // right {state}Styles bucket (entry animation stays base-only).
    "animations"
  ]
};

/**
 * Base (non-state, non-identity) effect fields a style-tag entry persists.
 * KOA-18159: the tag paths used to write only isVisible+opacity, dropping
 * everything an Effect Preset produces. Add new effect fields here, not at the
 * call sites; keep in sync with `STATE_ALLOWED_PROPS.effects`.
 */
export const EFFECTS_BASE_FIELDS: readonly (keyof IStylingEffects)[] = [
  "isVisible",
  "opacity",
  "cursor",
  "pointerEvents",
  "mixBlendMode",
  "outlineStyle",
  "outlineWidth",
  "outlineColor",
  "outlineOffset",
  "transforms",
  "transitions",
  "transformOriginX",
  "transformOriginY",
  "perspective",
  "childrenPerspective",
  "childrenPerspectiveOriginX",
  "childrenPerspectiveOriginY",
  "backfaceVisibility",
  "transformStyle",
  "filters",
  "backdropFilters",
  "entryAnimation",
  "animations"
] as const;

/**
 * Every base effect field off a form section. Undefined is copied through on
 * purpose — that is how a cleared value is cleared on the tag. Callers must skip
 * the write when the form has no `effects` section at all (collapsed panel →
 * antd registers nothing), else the all-undefined result wipes the tag.
 */
export function pickEffectsBaseProps(source: IStylingEffects): Partial<IStylingEffects> {
  const result: Partial<IStylingEffects> = {};
  const target = result as unknown as Record<string, unknown>;
  const form = source as unknown as Record<string, unknown>;
  EFFECTS_BASE_FIELDS.forEach((field: keyof IStylingEffects): void => {
    target[field as string] = form[field as string];
  });
  return result;
}

/**
 * Where a tag save writes, decided from the edit context — not from
 * `form.styleGroupId`, which a state switch can drop. KOA-18159: without an id
 * the save used to fall into the legacy branch, where a nullish groupId made
 * `find(s => s.groupId === groupId)` hit the first entry that has no groupId
 * (a shared tag like `Button_Fill`) and overwrite it. `"none"` = write nothing.
 */
export type TagSaveTarget = "unified" | "legacy" | "none";

export function resolveTagSaveTarget(editingGroupId: string | null, groupId: string | null | undefined): TagSaveTarget {
  const isUnifiedTag: boolean = editingGroupId === "unified" || (!editingGroupId && !!groupId);
  if (!isUnifiedTag) return "legacy";
  return groupId ? "unified" : "none";
}

/**
 * Per-section fields preserved across a base-reset operation. Mirrors the
 * exclude list used by resetSectionChanges so the two reset paths agree
 * on what counts as a "control/metadata" field versus a real style value.
 */
export const BASE_RESET_EXCLUDE_FIELDS: Readonly<Record<string, readonly string[]>> = {
  position: ["positionIds", "display"],
  text: ["typoIds", "fontSizeStyle"],
  shadow: ["shadowIds", "isVisible"],
  border: ["borderIds", "borderStyle", "isBorderRadiusLocked", "isBorderWidthLocked"],
  fill: ["fillIds", "bgImagePosition", "bgImageSize", "repeatSwitch", "stretch"],
  effects: ["effectsIds", "isVisible"]
};

/**
 * Copies hoverStyles / pressedStyles / focusedStyles / disabledStyles
 * from `source` into `target`, deep-cloning every state sub-object.
 *
 * KOA-15838 root-cause fix: an earlier version did
 *   `targetSection.hoverStyles = sourceSection.hoverStyles;`
 * which aliased the target's state sub-objects to the live DI's. Any
 * mutation through the preview-write path then bled across components
 * because every reload shared the same nested references. Deep-cloning
 * here makes the persist pipeline the only mutation channel into the DI.
 */
export function preserveStateStylesFromSource(target: IKuikaStyle, source: IKuikaStyle | undefined): void {
  if (!source) return;

  STYLE_SECTIONS.forEach((section) => {
    if (target[section] && source[section]) {
      const targetSection = target[section] as unknown as Record<string, unknown>;
      const sourceSection = source[section] as unknown as Record<string, unknown>;
      if (sourceSection.hoverStyles) targetSection.hoverStyles = _.cloneDeep(sourceSection.hoverStyles);
      if (sourceSection.pressedStyles) targetSection.pressedStyles = _.cloneDeep(sourceSection.pressedStyles);
      if (sourceSection.focusedStyles) targetSection.focusedStyles = _.cloneDeep(sourceSection.focusedStyles);
      if (sourceSection.disabledStyles) targetSection.disabledStyles = _.cloneDeep(sourceSection.disabledStyles);
    }
  });
}

/**
 * Gives `backup` an EMPTY base for every section `current` has but `backup` lacks.
 *
 * `backup` is the pre-state-mode snapshot. A section missing from it was first created DURING
 * state mode — its base is pure preview mirror, so the clean base is empty (the same rule
 * `buildRestoredStyleObj(..., emptyMissingBackupSections=true)` applies on state→state switches).
 * Leaving it out made the exit-to-None path keep the form's state values as the base: a hover-only
 * Effects transform on a component that had no Effects before became its base transform.
 * Only for a full DI snapshot — an edit-mode tag backup legitimately omits sections.
 */
export function addEmptyBaseForSectionsCreatedInState(backup: IKuikaStyle, current: IKuikaStyle | undefined): void {
  if (!current) return;
  STYLE_SECTIONS.forEach((section) => {
    if (!backup[section] && current[section]) {
      (backup as unknown as Record<string, unknown>)[section] = {};
    }
  });
}

/**
 * Lifts allow-listed fields from `formData` into `styleObj[section][stateProp]`.
 * When `updateBaseForPreview` is true, the same fields are also written
 * onto the base section so the canvas reflects the state-mode edit
 * immediately; the backup mechanism in the orchestrator restores those
 * base values when the user leaves state mode.
 */
export function saveFormDataToStateStyles(
  styleObj: IKuikaStyle,
  formData: IKuikaStyle,
  stateProp: string,
  updateBaseForPreview: boolean = false
): void {
  STYLE_SECTIONS.forEach((section) => {
    if (!formData[section]) return;
    if (!styleObj[section]) (styleObj as unknown as Record<string, unknown>)[section] = {};
    const currentSection = styleObj[section] as unknown as Record<string, unknown>;
    if (!currentSection[stateProp]) currentSection[stateProp] = {};

    const sectionData = formData[section] as unknown as Record<string, unknown>;
    const allowed = STATE_ALLOWED_PROPS[section] || [];
    const stateTarget = currentSection[stateProp] as Record<string, unknown>;
    const cleanProps: Record<string, unknown> = {};
    // Reconcile (not merge) the allow-listed keys against the form. A key that
    // is present sets/overwrites the state value; a key the user CLEARED (trash
    // icon → undefined) is DELETED from the state sub-object. KOA-15838: the old
    // `Object.assign(stateTarget, cleanProps)` only ever ADDED defined keys, so a
    // cleared hover/pressed/focused/disabled prop kept its stale value and
    // reappeared the next time the state loaded into the form.
    allowed.forEach((key) => {
      const incoming: unknown = sectionData[key];
      if (incoming !== undefined) {
        // Deep-clone so neither the persisted state bucket nor the base mirror
        // aliases the form payload's nested arrays/objects. Faz 3 effects carry
        // multi-item arrays (transforms / filters); a later mutation of the form
        // data must never reach into the DI's persisted *Styles.
        cleanProps[key] = _.cloneDeep(incoming);
        stateTarget[key] = _.cloneDeep(incoming);
      } else {
        delete stateTarget[key];
      }
    });

    // Drop a now-empty state sub-object so the state-tab "changed" indicator
    // (hasStateStyles) and the backend GenerateStateBlock never see a hollow {}.
    if (Object.keys(stateTarget).length === 0) {
      delete (currentSection as Record<string, unknown>)[stateProp];
    }

    // Base mirror stays additive: buildRestoredStyleObj already reset base from
    // the clean statePreviewBackup, so a cleared key correctly reverts to its
    // base value — nothing to delete here.
    if (updateBaseForPreview) Object.assign(currentSection, cleanProps);
  });
}

/**
 * KOA-9069: a single value as the styling form carries it into a section bag. Spelled out as a
 * concrete union rather than `unknown` — the section keys are genuinely open-ended (they come
 * from the `STATE_ALLOWED_PROPS` arrays), but the values are not.
 */
type StyleSectionValue =
  | string
  | string[]
  | number
  | boolean
  | IFilterItem[]
  | ILoopingAnimationItem[]
  | ITransformItem[]
  | ITransitionItem[]
  | null
  | undefined;

/** KOA-9069: one conditional style viewed as its open-ended section map. */
type ConditionalStyleSections = Record<string, Record<string, StyleSectionValue>>;

/**
 * KOA-9069: finds a conditional style by id. Returns `null` rather than an index so a caller
 * can never act on a stale position — the list is reorderable and ids are the identity.
 */
export function findConditionalStyle(
  styleObj: IKuikaStyle | undefined,
  conditionalStyleId: string
): IConditionalStyle | null {
  const conditionalStyles: IConditionalStyle[] | undefined = styleObj?.conditionalStyles;
  if (!conditionalStyles) return null;

  return conditionalStyles.find((entry: IConditionalStyle): boolean => entry?.id === conditionalStyleId) ?? null;
}

/** What a form reload shows, and the conditional style it leaves the form bound to. */
export interface IFormReloadTarget {
  formData: IKuikaStyle;
  conditionalStyleId: string | null;
}

/**
 * The form values a reload (undo, a property change, a write from the selection bar) must show. While
 * the form is bound to a conditional style the reload re-reads THAT condition: loading the base style
 * instead left the panel bound to the condition with base values in it, and its next edit saved them
 * into the condition. A condition that no longer exists (undone, removed elsewhere) releases the form
 * to the base style. `baseFormData` is the component's base style in form shape; its tag ids carry over.
 */
export function resolveFormReloadTarget(
  styleObj: IKuikaStyle | undefined,
  baseFormData: IKuikaStyle,
  selectedConditionalStyleId: string | null
): IFormReloadTarget {
  if (selectedConditionalStyleId === null || findConditionalStyle(styleObj, selectedConditionalStyleId) === null) {
    return { formData: baseFormData, conditionalStyleId: null };
  }
  return {
    formData: loadConditionalStyleIntoFormData(styleObj, selectedConditionalStyleId, baseFormData),
    conditionalStyleId: selectedConditionalStyleId
  };
}

/**
 * KOA-9069: the name shown for a conditional style. Falls back to its position so a rule saved
 * before naming existed, or one the user has not named yet, still has something to show.
 */
export function conditionalStyleDisplayName(entry: IConditionalStyle, index: number): string {
  const name: string = entry?.name?.trim() ?? "";
  return name.length > 0 ? name : `Condition ${index + 1}`;
}

/**
 * KOA-9069: true when this one rule actually sets any CSS. A named condition with nothing on it
 * does nothing at runtime, so the list marks it rather than letting it pass for a working rule.
 */
export function conditionalStyleHasStyles(entry: IConditionalStyle): boolean {
  if (!entry) return false;
  const sections: ConditionalStyleSections = entry as unknown as ConditionalStyleSections;

  return STYLE_SECTIONS.some((section: keyof IKuikaStyle): boolean => {
    const sectionData: Record<string, StyleSectionValue> | undefined = sections[section as string];
    return sectionData !== undefined && Object.keys(sectionData).length > 0;
  });
}

/** KOA-9069: the outcome of checking a conditional style's name, ready to render under the field. */
export interface IConditionalStyleNameValidation {
  isValid: boolean;
  /** User-facing reason, absent when valid. */
  message?: string;
}

/**
 * KOA-9069: the naming rule for a conditional style — required, unique on this component, and the
 * platform's own identifier shape (letters, digits and spaces, starting with a letter), matching
 * how workflows and screens are named.
 *
 * Comparison is `toLowerCase`, which is locale-independent in JS: `toLocaleLowerCase` would make
 * "Iade" and "iade" collide differently under a Turkish locale than an English one, so two
 * developers would disagree about whether the same name is a duplicate.
 */
export function validateConditionalStyleName(
  name: string,
  conditionalStyles: IConditionalStyle[],
  conditionalStyleId: string
): IConditionalStyleNameValidation {
  const trimmed: string = (name ?? "").trim();

  if (trimmed.length === 0) {
    return { isValid: false, message: "Give the condition a name." };
  }

  // KOA-9069 QA (B3): the shape test runs on the value with only TRAILING space removed, so a
  // LEADING space fails the "start with a letter" rule like any other bad first character.
  // Trailing space is forgiven because it is a transient keystroke while typing a multi-word
  // name — flashing an error between "Gec Kalan " and "Gec Kalan Siparis" would be noise.
  const withoutTrailingSpace: string = (name ?? "").replace(/\s+$/, "");

  if (!/^[A-Za-z][A-Za-z0-9 ]*$/.test(withoutTrailingSpace)) {
    return { isValid: false, message: "Use letters, digits and spaces. Start with a letter." };
  }

  const clash: IConditionalStyle | undefined = conditionalStyles.find(
    (entry: IConditionalStyle): boolean =>
      entry?.id !== conditionalStyleId && (entry?.name ?? "").trim().toLowerCase() === trimmed.toLowerCase()
  );

  if (clash) {
    return { isValid: false, message: `This component already has a condition called “${trimmed}”.` };
  }

  return { isValid: true };
}

/**
 * KOA-9069: carries over the `styleObj` properties the styling FORM never holds.
 *
 * `handleStyleFormFinish` builds the base ("none") write as `{ ...formData }`. The form binds only
 * the six style sections, so anything else on `styleObj` is simply absent from that object and the
 * write ERASES it — which silently deleted every conditional style on a component the moment any
 * base style was committed. The state and condition branches escaped it only because they happen to
 * start from a clone of the DI.
 *
 * Keep this list in sync with every `IKuikaStyle` property that is owned by the display item rather
 * than by the form. Same trap as `convertFormStyleToDiStyle`, which rebuilds from a key literal.
 */
export function preserveDiOwnedStyleProps(target: IKuikaStyle, source: IKuikaStyle | undefined): void {
  if (!source) return;

  if (source.conditionalStyles !== undefined) {
    target.conditionalStyles = _.cloneDeep(source.conditionalStyles);
  }
  if (source.autoIncrementedIndex !== undefined) {
    target.autoIncrementedIndex = source.autoIncrementedIndex;
  }
}

/**
 * KOA-9069: true when the component has at least one conditional style carrying real CSS.
 * Mirrors the state-mode "changed" indicator — a hollow entry must not light the tab up, and
 * must not reach `GenerateStateBlock`, which would emit an empty rule for it.
 */
export function hasConditionalStyles(styleObj: IKuikaStyle | undefined): boolean {
  const conditionalStyles: IConditionalStyle[] | undefined = styleObj?.conditionalStyles;
  if (!conditionalStyles || conditionalStyles.length === 0) return false;

  return conditionalStyles.some(conditionalStyleHasStyles);
}

/**
 * KOA-9069: moves a conditional style one place up or down, returning a NEW array.
 *
 * Order is precedence: the generator emits `.kuika-cond-{i}` in array order and, at equal CSS
 * specificity, the later rule wins. There is deliberately no separate `priority` field — a second
 * source of truth could disagree with the array and there would be no way to say which is right.
 *
 * Safe against a reorder because every other path addresses entries by `id`, never by position;
 * only the emitted class indices shift, and CSS and predicates are generated from this same array
 * in one pass, so they shift together.
 *
 * A move that would fall off either end returns the list unchanged rather than wrapping around.
 */
export function moveConditionalStyle(
  conditionalStyles: IConditionalStyle[],
  conditionalStyleId: string,
  direction: "up" | "down"
): IConditionalStyle[] {
  const index: number = conditionalStyles.findIndex(
    (entry: IConditionalStyle): boolean => entry?.id === conditionalStyleId
  );
  if (index === -1) return conditionalStyles;

  const targetIndex: number = direction === "up" ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= conditionalStyles.length) return conditionalStyles;

  const reordered: IConditionalStyle[] = [...conditionalStyles];
  const [moved] = reordered.splice(index, 1);
  reordered.splice(targetIndex, 0, moved);

  return reordered;
}

/**
 * KOA-9069: lifts allow-listed fields from `formData` into the conditional style with this id.
 *
 * Deliberately a sibling of `saveFormDataToStateStyles` rather than a parameter on it. Both
 * reconcile against the SAME `STATE_ALLOWED_PROPS` allow-list — a conditional style may layer
 * exactly what a state style may layer — but they write to different shapes: state styles are
 * grouped by section (`fill.hoverStyles`), conditional styles by condition
 * (`conditionalStyles[i].fill`), because one condition sets background AND font colour together
 * and the user thinks of that as one thing.
 *
 * Reconcile, never merge: a key the user cleared is DELETED, which is the KOA-15838 lesson —
 * an additive `Object.assign` left stale values that reappeared on the next load.
 *
 * Unlike the state path there is no base mirror. A condition has no single truth to preview
 * against on the canvas (it is true for some rows and false for others), so writing it onto
 * base would paint every row with a style only some of them earn.
 */
export function saveFormDataToConditionalStyle(
  styleObj: IKuikaStyle,
  formData: IKuikaStyle,
  conditionalStyleId: string
): void {
  const target: IConditionalStyle | null = findConditionalStyle(styleObj, conditionalStyleId);
  if (!target) return;

  const targetSections: ConditionalStyleSections = target as unknown as ConditionalStyleSections;

  STYLE_SECTIONS.forEach((section: keyof IKuikaStyle): void => {
    if (!formData[section]) return;
    if (!targetSections[section as string]) targetSections[section as string] = {};

    const sectionData: Record<string, StyleSectionValue> = formData[section] as unknown as Record<
      string,
      StyleSectionValue
    >;
    const allowed: readonly string[] = STATE_ALLOWED_PROPS[section as string] || [];
    const sectionTarget: Record<string, StyleSectionValue> = targetSections[section as string];

    allowed.forEach((key: string): void => {
      const incoming: StyleSectionValue = sectionData[key];
      if (incoming !== undefined) {
        // Deep-clone for the same reason the state path does: effects carry multi-item arrays
        // and the persisted bucket must not alias the live form payload.
        sectionTarget[key] = _.cloneDeep(incoming);
      } else {
        delete sectionTarget[key];
      }
    });

    if (Object.keys(sectionTarget).length === 0) {
      delete targetSections[section as string];
    }
  });
}

/**
 * KOA-9069: projects a conditional style back into the flat shape the styling form binds to,
 * so selecting a condition loads its own values instead of the base ones.
 */
/**
 * KOA-9069 QA (round 2): the per-section field naming WHICH style tags the component wears.
 *
 * It describes the component, not whichever overlay the form happens to be editing, so every
 * write-target switch has to carry it across untouched. `handleStateChange` already does that by
 * hand (its `existingIds` spread) — this is the same identity, named once so the condition path
 * cannot drift from the state path again.
 *
 * It lives ONLY in the form. The DI persists a single `|`-separated `positionId` string, and
 * `StyleHelper.convertIStylingPositionToIFormStylingPosition` explodes it into `positionIds` and
 * nulls the original on the way in. Read these off a `styleObj` and you get `undefined` every
 * time — so the live form value is the identity source, never the DI.
 */
const STYLE_TAG_IDS_FIELD: Readonly<Record<string, string>> = {
  position: "positionIds",
  text: "typoIds",
  fill: "fillIds",
  border: "borderIds",
  shadow: "shadowIds",
  effects: "effectsIds"
};

/**
 * KOA-9069 QA (round 2): copies the style-tag identity of every section from `identitySource` onto
 * `target`, creating sections as needed. The single primitive both write-target switches use.
 */
export function carryStyleTagIds(target: IKuikaStyle, identitySource: IKuikaStyle | undefined): void {
  if (!identitySource) return;

  const sourceSections: ConditionalStyleSections = identitySource as unknown as ConditionalStyleSections;
  const targetSections: ConditionalStyleSections = target as unknown as ConditionalStyleSections;

  STYLE_SECTIONS.forEach((section: keyof IKuikaStyle): void => {
    const idsField: string = STYLE_TAG_IDS_FIELD[section as string];
    const tagIds: StyleSectionValue | undefined = sourceSections[section as string]?.[idsField];
    if (tagIds === undefined) return;

    if (!targetSections[section as string]) targetSections[section as string] = {};
    targetSections[section as string][idsField] = _.cloneDeep(tagIds);
  });
}

export function loadConditionalStyleIntoFormData(
  styleObj: IKuikaStyle | undefined,
  conditionalStyleId: string,
  currentFormValue?: IKuikaStyle
): IKuikaStyle {
  const source: IConditionalStyle | null = findConditionalStyle(styleObj, conditionalStyleId);
  const formData: IKuikaStyle = {};

  const sourceSections: ConditionalStyleSections | undefined = (source ?? undefined) as unknown as
    | ConditionalStyleSections
    | undefined;
  const formSections: ConditionalStyleSections = formData as unknown as ConditionalStyleSections;

  // EVERY section is emitted, including one the condition does not touch. The caller resets the
  // form before applying this, so a section left out comes back with no tag ids at all: the panel
  // reads them straight back out of the form, renders the component as "No style", and collapses
  // the whole Position/Text/Fill/Border/Shadow stack. The next base-targeted save then persists
  // that emptiness and the component really has lost its styles. A section the condition does not
  // override is therefore emitted EMPTY-BUT-IDENTIFIED, which is exactly the "clear" contract
  // `resolveSectionLoadMode` already defines for the state path ("ids preserved").
  STYLE_SECTIONS.forEach((section: keyof IKuikaStyle): void => {
    const sectionData: Record<string, StyleSectionValue> | undefined = sourceSections?.[section as string];
    formSections[section as string] = sectionData ? _.cloneDeep(sectionData) : {};
  });

  carryStyleTagIds(formData, currentFormValue);

  return formData;
}

/**
 * Builds a clone of `currentStyleObj` whose base sections are wholesale
 * replaced with the pre-state-edit backup while persisted state
 * sub-objects from `current` are preserved. Wholesale replacement (vs.
 * key-by-key merge) drops any property the state-mode preview added to
 * base but was never present in the original — e.g. previewing
 * `textDecoration: "underline"` on hover when base never set
 * textDecoration would otherwise leak into the persisted base.
 */
export function buildRestoredStyleObj(
  currentStyleObj: IKuikaStyle,
  backup: IKuikaStyle,
  emptyMissingBackupSections: boolean = false
): IKuikaStyle {
  const result = _.cloneDeep(currentStyleObj) as IKuikaStyle;

  STYLE_SECTIONS.forEach((section) => {
    const backupSection = backup[section] as unknown as Record<string, unknown> | undefined;
    const resultSection = result[section] as unknown as Record<string, unknown> | undefined;
    if (!resultSection) return;

    // A section present in `current` but absent in `backup` was first touched
    // DURING state mode (the backup is the pre-state snapshot), so its base is
    // pure preview mirror — the clean base is empty. Callers restoring a FULL
    // DI snapshot pass `emptyMissingBackupSections=true` to wipe that mirror.
    // Callers whose backup only covers SOME sections (edit-mode tag sourceStyle,
    // which legitimately omits the sections the tag doesn't define) keep the
    // default `false` so untouched sections survive unchanged.
    if (!backupSection && !emptyMissingBackupSections) return;

    const preservedStateStyles: Record<string, unknown> = {};
    if (resultSection.hoverStyles) preservedStateStyles.hoverStyles = resultSection.hoverStyles;
    if (resultSection.pressedStyles) preservedStateStyles.pressedStyles = resultSection.pressedStyles;
    if (resultSection.focusedStyles) preservedStateStyles.focusedStyles = resultSection.focusedStyles;
    if (resultSection.disabledStyles) preservedStateStyles.disabledStyles = resultSection.disabledStyles;

    let backupBaseProps: Record<string, unknown> = {};
    if (backupSection) {
      const { hoverStyles: _bh, pressedStyles: _bp, focusedStyles: _bf, disabledStyles: _bd, ...rest } = backupSection;
      backupBaseProps = rest;
    }

    (result as unknown as Record<string, unknown>)[section] = {
      ...backupBaseProps,
      ...preservedStateStyles
    };
  });

  return result;
}

/** Snapshot of the DI selected at tag-edit entry, restored on tag-edit exit. */
export interface IEditDiBackup {
  kuikaId: string;
  styleObj: IKuikaStyle;
}

export interface ISelectionChangeRestoreParams {
  /** Whether a style tag was being edited when the selection changed. */
  isEditMode: boolean;
  /** Clean pre-edit DI snapshot captured at tag-edit entry (edit-mode path). */
  editDiBackup: IEditDiBackup | null;
  /** Active panel state at the moment selection changed. */
  selectedState: StyleState;
  /** Clean pre-state-mode base snapshot (non-edit per-DI state path). */
  statePreviewBackup: IKuikaStyle | null;
  /** The previously-selected DI's live styleObj (preview-corrupted in state mode). */
  prevStyleObj: IKuikaStyle | undefined;
  /** kuikaId of the previously-selected DI. */
  prevKuikaId: string;
}

/**
 * Decide which styleObj (and on which DI) must be committed to undo the live
 * preview the PREVIOUS DI carried when the user switched selection. Two mutually
 * exclusive cases, both keyed on the PREVIOUS DI's own kuikaId so the restore is
 * correct even though the selection already moved:
 *
 * 1. **Tag edit in progress** — discard the unsaved edit by restoring the clean
 *    pre-edit snapshot (`editDiBackup`). The tag's project entries were never
 *    mutated, so this fully cancels the edit. (Returns null if no backup was
 *    captured — e.g. selection had no styleObj when edit began.)
 * 2. **Per-DI state mode** (not editing a tag) — rebuild the previous DI's clean
 *    base from `statePreviewBackup`, dropping the state-mode preview mirror.
 *
 * Returns null when nothing needs restoring (plain none-mode selection change).
 * Pure: no React/state access, so it is unit-tested directly.
 */
export function resolveSelectionChangeRestore(params: ISelectionChangeRestoreParams): IEditDiBackup | null {
  const { isEditMode, editDiBackup, selectedState, statePreviewBackup, prevStyleObj, prevKuikaId } = params;
  if (isEditMode) {
    if (!editDiBackup) return null;
    return { kuikaId: editDiBackup.kuikaId, styleObj: editDiBackup.styleObj };
  }
  if (selectedState !== "none" && statePreviewBackup && prevStyleObj) {
    return { kuikaId: prevKuikaId, styleObj: buildRestoredStyleObj(prevStyleObj, statePreviewBackup, true) };
  }
  return null;
}

/**
 * BFS the display-item tree for the DI whose id matches `kuikaId`. Kept local
 * (rather than reusing ScreenDesignHelper) so this module stays free of the
 * heavy import chain — ScreenDesignHelper transitively pulls `window`, which
 * breaks the node-environment unit tests this module relies on.
 */
function findDisplayItemByKuikaId(kuikaId: string, displayItems: IDisplayItem[]): IDisplayItem | undefined {
  if (!kuikaId || !displayItems) return undefined;
  const queue: IDisplayItem[] = [...displayItems];
  while (queue.length > 0) {
    const item: IDisplayItem = queue.shift()!;
    if (item.id.toString() === kuikaId) return item;
    if (item.children) queue.push(...item.children);
  }
  return undefined;
}

/**
 * Build a serialization-safe copy of the screen design before it leaves the
 * designer (backend save + codegen). While the styling panel is in a non-none
 * state, the selected DI's base styleObj has been mirror-corrupted with the
 * active state's values to drive the live canvas preview (see
 * `saveFormDataToStateStyles` with `updateBaseForPreview=true`). The clean base
 * lives only in `statePreviewBackup`. This returns a deep clone with that one
 * DI's base rebuilt from the backup (state sub-objects preserved), so the
 * persisted / generated output never carries the preview mirror.
 *
 * The input is NEVER mutated — the live tree and canvas are left untouched. In
 * "none" mode (or with no backup / no selection) the input is returned
 * unchanged, so there is no clone cost on the common save path.
 */
export function buildSerializableScreenDesign(
  screenDesign: IScreenDesign,
  selectedState: StyleState,
  statePreviewBackup: IKuikaStyle | null,
  kuikaId: string | undefined,
  isEditMode: boolean
): IScreenDesign {
  // Edit mode: the selected DI carries a style-tag preview that
  // handleEditModeChange restores on exit (editDiBackup). Leave it untouched —
  // the tag backup only covers some sections, so cleaning here could wipe
  // unrelated base values. (Edit-mode save leak is a separate, pre-existing
  // concern handled by editDiBackup, not by this serialize-time guard.)
  if (isEditMode || selectedState === "none" || !statePreviewBackup || !kuikaId || !screenDesign?.displayItems) {
    return screenDesign;
  }
  const clone: IScreenDesign = _.cloneDeep(screenDesign);
  const di: IDisplayItem | undefined = findDisplayItemByKuikaId(kuikaId.toString(), clone.displayItems);
  if (di?.styleObj) {
    // Full DI snapshot backup → empty the base of any section the user first
    // touched in state mode (its base is pure preview mirror).
    di.styleObj = buildRestoredStyleObj(di.styleObj, statePreviewBackup, true);
  }
  return clone;
}

/**
 * How `handleStateChange` should populate ONE styling section's form fields
 * when switching to `state`, given whether `sourceStyle` actually supplies
 * that section.
 *
 * - `"skip"`     → leave the current form value untouched (nothing to load).
 * - `"clear"`    → emit an EMPTY section (ids preserved) so stale values drop.
 * - `"loadBase"` → load the section's base props (none state).
 * - `"loadState"`→ load the `{state}Styles` overlay for the active state.
 */
export type SectionLoadMode = "skip" | "clear" | "loadBase" | "loadState";

/**
 * KOA-15838 leak fix — the single decision the form-leak hinges on.
 *
 * When `sourceStyle` cannot supply a section (`hasSource === false`) and the
 * user switches INTO a non-none state, the section MUST be CLEARED rather than
 * skipped: the old `if (!srcSection) return` left the previous (none) values in
 * the form, leaking base colours/margins/etc. into hover/pressed/focused/
 * disabled. This happens whenever the edited tag can't map to a section — a
 * single-panel (legacy) tag whose id matches only one section, or a partial
 * unified tag missing some collections.
 *
 * For the none state there is no overlay to show, so an absent source means
 * "nothing to load" → `"skip"` (keep whatever the form already holds).
 */
/**
 * KOA-9069 QA (round 2) — which style the state switch should patch.
 *
 * `handleStateChange` repopulates the form by PATCHING whatever it currently holds: it keeps a
 * section the incoming state cannot supply (`resolveSectionLoadMode` → `"skip"`) and re-attaches
 * the tag ids it reads back out of that same form. Both steps assume the form holds the
 * component's own base style.
 *
 * While a condition is selected it does not. The form then holds that condition's sparse overlay,
 * so patching it carries the condition's values into the base-targeted submit `handleStateChange`
 * fires on its way out — the condition's red lands on the component itself — and re-reads tag ids
 * from a form that is mid-condition. The DI is the component's own truth; seed from it instead.
 */
export function resolveStateSwitchFormSeed(
  selectedConditionalStyleId: string | null,
  diStyleObj: IKuikaStyle | undefined,
  currentFormValue: IKuikaStyle
): IKuikaStyle {
  if (selectedConditionalStyleId === null) return currentFormValue;

  // DI for the VALUES, form for the IDENTITY: the DI has no `*Ids` at all (see
  // STYLE_TAG_IDS_FIELD), so seeding from it alone would blank the component's tags.
  const seed: IKuikaStyle = _.cloneDeep(diStyleObj ?? {}) as IKuikaStyle;
  carryStyleTagIds(seed, currentFormValue);
  return seed;
}

export function resolveSectionLoadMode(hasSource: boolean, state: string): SectionLoadMode {
  if (state === "none") {
    return hasSource ? "loadBase" : "skip";
  }
  return hasSource ? "loadState" : "clear";
}

// ───────────────────────────────────────────────────────────────────────────
// KOA-15838 — "None" state indicator (base-style detection)
//
// The state selectbox lights an orange dot next to each state that carries
// edits. For the "None" entry that means "the BASE section props were changed".
//
// CRITICAL: while the panel is in a non-none state, the selected DI's base
// section props are PREVIEW-CORRUPTED — `handleStyleFormFinish` mirrors the
// active state's overlay onto base (`saveFormDataToStateStyles` with
// `updateBaseForPreview=true`) so the canvas previews the state live. Reading
// that corrupted base lights the "None" dot for values that belong to the
// state, not the base (the original false-dot bug). The CLEAN base in state
// mode is `statePreviewBackup` (the snapshot taken at state-entry), so the
// detection must read it instead of the DI — matching the invariant
// "statePreviewBackup is the ONLY clean source of base values during state mode".
// ───────────────────────────────────────────────────────────────────────────

/** Per-section id field — a section carrying its own id is LINKED to a tag. */
const BASE_STYLE_ID_FIELD: Readonly<Record<string, string>> = {
  position: "positionId",
  text: "typoId",
  fill: "fillId",
  border: "borderId",
  shadow: "shadowId",
  effects: "effectsId"
};

/**
 * Per-section control/metadata keys that must NOT count as a base style value
 * when deciding whether the "None" indicator lights. Mirrors the per-section
 * exclude lists the panel applied inline before this was extracted.
 */
const BASE_STYLE_DETECT_EXCLUDE: Readonly<Record<string, readonly string[]>> = {
  position: [
    "positionId",
    "positionIds",
    "display",
    "name",
    "isPaddingLocked",
    "widthStyle",
    "heightStyle",
    "maxWidthStyle",
    "maxHeightStyle",
    "minWidthStyle",
    "minHeightStyle",
    "componentType",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles",
    "id",
    "groupId"
  ],
  text: [
    "typoId",
    "typoIds",
    "fontSizeStyle",
    "name",
    "componentType",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles",
    "id",
    "groupId"
  ],
  fill: [
    "fillId",
    "fillIds",
    "bgImagePosition",
    "bgImageSize",
    "repeatSwitch",
    "stretch",
    "name",
    "imageFit",
    "componentType",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles",
    "id",
    "groupId"
  ],
  border: [
    "borderId",
    "borderIds",
    "borderStyle",
    "isBorderRadiusLocked",
    "isBorderWidthLocked",
    "name",
    "componentType",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles",
    "id",
    "groupId"
  ],
  shadow: [
    "shadowId",
    "shadowIds",
    "isVisible",
    "name",
    "componentType",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles",
    "id",
    "groupId"
  ],
  effects: [
    "effectsId",
    "effectsIds",
    "isVisible",
    "name",
    "componentType",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles",
    "id",
    "groupId"
  ]
};

/** Bookkeeping fields present on project style entries (not on form values). */
const BASE_STYLE_DETECT_META: readonly string[] = [
  "styleType",
  "kuikaid",
  "overridden",
  "name",
  "componentType",
  "groupId",
  "id"
];

/** A base value counts only if not null/empty/transparent (recurses arrays/objects). */
function isNonEmptyBaseValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "" || value === "transparent") return false;
  if (Array.isArray(value)) return value.length > 0 && value.some(isNonEmptyBaseValue);
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    return Object.keys(obj).length > 0 && Object.values(obj).some(isNonEmptyBaseValue);
  }
  return true;
}

/**
 * True when one section carries at least one genuine base style prop.
 *
 * `ignoreIdGuard=false`: a section that references a tag (carries its own id)
 * is LINKED, not locally changed → returns false. `ignoreIdGuard=true`: count
 * a section even when it references a tag, as long as it ALSO holds a non-id,
 * non-control base prop (a pure reference — only ids — still yields false
 * because the id fields are in the exclude list).
 */
export function sectionHasBaseStyle(
  sectionData: Record<string, unknown> | undefined,
  section: string,
  ignoreIdGuard: boolean
): boolean {
  if (!sectionData) return false;
  const idField = BASE_STYLE_ID_FIELD[section];
  if (!ignoreIdGuard && idField && sectionData[idField]) return false;
  const excluded = [...(BASE_STYLE_DETECT_EXCLUDE[section] || []), ...BASE_STYLE_DETECT_META];
  return Object.keys(sectionData).some((key) => !excluded.includes(key) && isNonEmptyBaseValue(sectionData[key]));
}

/** True when any section of `styleObj` carries a genuine base style prop. */
export function styleObjHasBaseStyle(styleObj: IKuikaStyle | undefined): boolean {
  if (!styleObj) return false;
  return STYLE_SECTIONS.some((section) =>
    sectionHasBaseStyle(styleObj[section] as unknown as Record<string, unknown> | undefined, section as string, true)
  );
}

/**
 * Selects the CLEAN base styleObj for the "None" indicator. In a non-none
 * state the DI's base is preview-corrupted, so `statePreviewBackup` (the
 * pre-state-mode snapshot) is the only clean source; in none mode (or when no
 * backup exists) the DI's own styleObj is already clean.
 */
export function resolveCleanBaseSource(
  selectedState: string,
  statePreviewBackup: IKuikaStyle | null | undefined,
  diStyleObj: IKuikaStyle | undefined
): IKuikaStyle | undefined {
  if (selectedState !== "none" && statePreviewBackup) return statePreviewBackup;
  return diStyleObj;
}

// ───────────────────────────────────────────────────────────────────────────
// KOA-15838 — tag-vs-inline state reconciliation
//
// A DI that REFERENCES a style tag (positionIds/typoIds/fillIds/borderIds/
// shadowIds) inherits that tag's state overlays (hover/pressed/focused/
// disabled) via the backend merge — it should NOT carry them inline. The
// edit-mode preview write used to freeze a tag's state value into the selected
// DI's `styleObj.{section}.{state}Styles`, producing a value that exactly
// equals the tag's. The helpers below detect that "inherited == inline"
// condition so the panel can (a) avoid the false orange "locally changed" dot
// and (b) strip the redundant inline copy on the next commit. Stripping is
// LOSSLESS: when the inline value equals what the tag resolves to, the backend
// merge produces byte-identical CSS with or without it.
// ───────────────────────────────────────────────────────────────────────────

/** The project-level styling collections, as exposed on `IProject`. */
export interface IStyleTagCollections {
  stylingPosition?: IStylingPosition[];
  stylingTypography?: IStylingTypography[];
  stylingFill?: IFillStyle[];
  stylingBorder?: IStylingBorder[];
  stylingShadow?: IStylingShadow[];
  stylingEffects?: IStylingEffects[];
}

interface ISectionTagMeta {
  /** DI section field holding the referenced tag id chain (e.g. "fillIds"). */
  idsField: string;
  /** Project entry's own id field (e.g. "fillId"). */
  entryIdField: string;
  /** Which collection on `IStyleTagCollections` holds this section's tags. */
  collectionField: keyof IStyleTagCollections;
}

const SECTION_TAG_META: Readonly<Record<string, ISectionTagMeta>> = {
  position: { idsField: "positionIds", entryIdField: "positionId", collectionField: "stylingPosition" },
  text: { idsField: "typoIds", entryIdField: "typoId", collectionField: "stylingTypography" },
  fill: { idsField: "fillIds", entryIdField: "fillId", collectionField: "stylingFill" },
  border: { idsField: "borderIds", entryIdField: "borderId", collectionField: "stylingBorder" },
  shadow: { idsField: "shadowIds", entryIdField: "shadowId", collectionField: "stylingShadow" },
  effects: { idsField: "effectsIds", entryIdField: "effectsId", collectionField: "stylingEffects" }
};

/** A style value counts as "set" only if it is not null/empty/transparent. */
function isNonEmptyStyleValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "" || value === "transparent") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return true;
}

/** Result of resolving a section's referenced tags into one state overlay. */
export interface IResolvedTagState {
  /** Merged per-prop value (backend precedence: later id in the chain wins). */
  value: Record<string, unknown>;
  /** Props where two+ tags in the chain disagree — precedence-sensitive. */
  ambiguous: Set<string>;
}

/**
 * Resolves the state overlay (`{state}Styles`) that a section's referenced
 * style tags contribute, mirroring the backend's merge precedence in
 * `ProjectExtensions.GetPositionStyle/GetFontStyle/...` → the style list is
 * `[DI inline, ids[last], …, ids[0]]` and the first non-null value per prop
 * wins, so LATER ids in the chain outrank earlier ones. The DI's own inline
 * overlay is intentionally excluded here — this is the TAG-only contribution
 * we compare the inline copy against.
 *
 * ⚠️ Keep in sync with `ProjectExtensions.cs` (C# merge). If the C# precedence
 * changes, this must change too, or the strip below could diverge.
 */
export function resolveTagStateStyles(
  collections: IStyleTagCollections | undefined,
  section: keyof IKuikaStyle,
  sectionData: Record<string, unknown> | undefined,
  stateProp: string
): IResolvedTagState {
  const empty: IResolvedTagState = { value: {}, ambiguous: new Set<string>() };
  if (!collections || !sectionData) return empty;
  const meta = SECTION_TAG_META[section as string];
  if (!meta) return empty;

  const ids = sectionData[meta.idsField];
  if (!Array.isArray(ids) || ids.length === 0) return empty;

  const collection = collections[meta.collectionField] as unknown as Record<string, unknown>[] | undefined;
  if (!collection || collection.length === 0) return empty;

  const value: Record<string, unknown> = {};
  const ambiguous = new Set<string>();

  for (let i = ids.length - 1; i >= 0; i--) {
    const id = ids[i];
    if (typeof id !== "string" || id.length === 0) continue;
    const idLower = id.toLowerCase();
    const entry = collection.find((s) => {
      const entryId = s[meta.entryIdField];
      return typeof entryId === "string" && (entryId as string).toLowerCase() === idLower;
    });
    const overlay = entry?.[stateProp] as Record<string, unknown> | undefined;
    if (!overlay) continue;
    for (const key of Object.keys(overlay)) {
      if (!isNonEmptyStyleValue(overlay[key])) continue;
      if (value[key] === undefined) {
        value[key] = overlay[key];
      } else if (!_.isEqual(value[key], overlay[key])) {
        ambiguous.add(key);
      }
    }
  }

  return { value, ambiguous };
}

/**
 * True when `overlay` carries at least one allow-listed CSS prop whose value
 * is a GENUINE local override — i.e. set, and different from what the
 * section's referenced tags resolve to. A value equal to the tag's is
 * INHERITED (class behaviour), not a change, so it does not count. Drives the
 * state-selector "changed" dot for a DI that references a tag.
 */
export function hasGenuineStateOverride(
  collections: IStyleTagCollections | undefined,
  section: keyof IKuikaStyle,
  sectionData: Record<string, unknown> | undefined,
  overlay: Record<string, unknown> | undefined,
  stateProp: string,
  allowedProps: readonly string[]
): boolean {
  if (!overlay) return false;
  const resolved = resolveTagStateStyles(collections, section, sectionData, stateProp);
  return allowedProps.some(
    (key) => isNonEmptyStyleValue(overlay[key]) && !_.isEqual(overlay[key], resolved.value[key])
  );
}

/**
 * Removes from `overlay` (mutated in place) every allow-listed prop whose value
 * equals the section's resolved tag value — the redundant inline copy left by
 * the edit-mode preview leak. Returns true if anything was removed.
 *
 * SAFETY: props the tag chain resolves AMBIGUOUSLY (two tags disagree, so
 * backend precedence is the only tiebreaker) are skipped — leaving the inline
 * copy is always safe; a wrong strip could change rendering.
 */
export function stripInheritedStateProps(
  collections: IStyleTagCollections | undefined,
  section: keyof IKuikaStyle,
  sectionData: Record<string, unknown> | undefined,
  overlay: Record<string, unknown>,
  stateProp: string,
  allowedProps: readonly string[]
): boolean {
  const resolved = resolveTagStateStyles(collections, section, sectionData, stateProp);
  let removed = false;
  for (const key of allowedProps) {
    if (resolved.ambiguous.has(key)) continue;
    if (isNonEmptyStyleValue(overlay[key]) && _.isEqual(overlay[key], resolved.value[key])) {
      delete overlay[key];
      removed = true;
    }
  }
  return removed;
}

const STATE_SUBOBJECT_KEYS: readonly string[] = ["hoverStyles", "pressedStyles", "focusedStyles", "disabledStyles"];

/**
 * Heals a DI's `styleObj` by stripping inherited (tag-equal) props from every
 * state sub-object of every section, and deleting any sub-object left empty.
 * Only props equal to the resolved tag value are removed — genuine overrides
 * (different from the tag) are preserved, and current rendering is unchanged
 * (the backend merge yields identical CSS). This is a deliberate DEDUP: an inline
 * value a user pinned that happens to EQUAL the tag is indistinguishable from a
 * leaked copy, so it is dropped too (a later tag edit will then move that element).
 * Mutates `styleObj` in place; returns true if anything changed.
 */
export function healInheritedStateStyles(
  styleObj: IKuikaStyle,
  collections: IStyleTagCollections | undefined
): boolean {
  let changed = false;
  STYLE_SECTIONS.forEach((section) => {
    const sectionData = styleObj[section] as unknown as Record<string, unknown> | undefined;
    if (!sectionData) return;
    const allowed = STATE_ALLOWED_PROPS[section as string] || [];
    STATE_SUBOBJECT_KEYS.forEach((stateProp) => {
      const overlay = sectionData[stateProp] as Record<string, unknown> | undefined;
      if (!overlay || typeof overlay !== "object") return;
      const removed = stripInheritedStateProps(collections, section, sectionData, overlay, stateProp, allowed);
      if (removed) changed = true;
      if (Object.keys(overlay).length === 0) {
        delete sectionData[stateProp];
        changed = true;
      }
    });
  });
  return changed;
}

/** Change reasons after which the panel re-reads the selected component into its form. */
const FORM_RELOAD_REASONS: ReadonlySet<ESdChangeReason> = new Set<ESdChangeReason>([
  ESdChangeReason.ChangeSelectedDiByCanvasFrame,
  ESdChangeReason.ChangeSelectedDiByTools,
  ESdChangeReason.DragDropDi,
  ESdChangeReason.DragDropBlock,
  ESdChangeReason.Undo,
  ESdChangeReason.Redo,
  ESdChangeReason.ChangePropertyValue,
  ESdChangeReason.LoadProject
]);

/**
 * What the styling panel does with an sd change:
 * - `reload` — re-read the component into the form;
 * - `dropPendingAndReload` — a styleObj was written OUTSIDE the panel (`ChangeStyleValueExternal`): the
 *   panel's pending debounced submit still carries the old values and would overwrite that write, so it
 *   is cancelled first. In state or tag-edit mode the base is a preview mirror restored from a backup,
 *   and in condition mode the form holds a conditional style, so the form is not re-read there
 *   (`dropPending`);
 * - `ignore` — nothing (the panel's own `ChangeStyleValue` commits land here: its form is already current).
 */
export type StyleFormSyncAction = "reload" | "dropPendingAndReload" | "dropPending" | "ignore";

/** Change reasons after which a styling section re-renders from the (re-read) form. */
const STYLE_SECTION_RENDER_REASONS: ReadonlySet<ESdChangeReason> = new Set<ESdChangeReason>([
  ESdChangeReason.ChangeSelectedDiByCanvasFrame,
  ESdChangeReason.ChangeSelectedDiByTools,
  ESdChangeReason.DragDropDi,
  ESdChangeReason.DragDropBlock,
  ESdChangeReason.ChangeStyleValueExternal
]);

/**
 * Whether a styling section (typography, fill, border, …) must re-render for an sd change. The
 * sections read the form inside `render()` (tab values, "has value" dots) rather than through bound
 * fields, so after the styling window re-reads a styleObj written outside the panel
 * (`ChangeStyleValueExternal`) they must repaint too — otherwise they keep the previous write's values.
 */
export function styleSectionNeedsRender(changeReason: ESdChangeReason): boolean {
  return STYLE_SECTION_RENDER_REASONS.has(changeReason);
}

export function resolveStyleFormSync(
  changeReason: ESdChangeReason,
  selectedState: StyleState,
  isEditMode: boolean,
  isConditionMode: boolean
): StyleFormSyncAction {
  if (changeReason === ESdChangeReason.ChangeStyleValueExternal) {
    return selectedState === "none" && !isEditMode && !isConditionMode ? "dropPendingAndReload" : "dropPending";
  }
  return FORM_RELOAD_REASONS.has(changeReason) ? "reload" : "ignore";
}
